import {
  AnyAssetApplication,
  ApiObjectPermissions,
  BaseObjects,
  EnrichedObjectPermissions,
  Flex,
  ObjectGroups,
  ObjectGroupsIds,
  openNotificationWithIcon,
  PageContent,
  PermissionGuard,
  Segment,
  useAssetTranslation,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import { Collapse, Empty, notification, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import { AppContext } from 'jsx';
import AddObjectModal from 'jsx/authorization/components/AddObjectModal';
import ObjectPermissionRow, {
  CHECKBOX_COLUMN_WIDTH,
} from 'jsx/authorization/components/ObjectPermissionRow';
import PageContainer from 'jsx/authorization/components/PageContainer';
import { Cell, RowHeader } from 'jsx/authorization/components/Table';
import Api from 'jsx/authorization/services/Api';
import {
  emptyObjectGroups,
  enrichObjectPermissionSets,
  getContentType,
  groupObjectByType,
  objectTypeToContentType,
  serializeToObjectIds,
  unsavedToObjPerms,
} from 'jsx/authorization/util/objectPermissions';
import {
  assetPermissions,
  PermissionSetOption,
  UserPermissionsAPI,
} from 'jsx/authorization/util/permissionsForEdit';
import Button from 'jsx/common/Button';
import React, { useContext, useEffect, useState } from 'react';
import { HardDrive, Trash } from 'react-feather';
import { useParams } from 'react-router-dom';

export type ParentKey = 'group' | 'user';
const AUTHORIZATION_NS = { ns: 'authorization' as const };
type Props = {
  userPermissions: any[];
  isGroup?: boolean;
  superAdminGroup?: boolean;
};

export default function ObjectPermissions({
  userPermissions,
  isGroup,
  superAdminGroup,
}: Props) {
  const parentType: ParentKey = isGroup ? 'group' : 'user';
  const params = useParams<any>();
  const parentId = params[`${parentType}Id`];
  const {t} = useAssetTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState(false);
  const [objectPermissions, setOP] = useState<ApiObjectPermissions>({});
  const [existingObjects, setObjects] = useState<EnrichedObjectPermissions>({});
  const [unsavedRows, setUnsavedRows] = useState<EnrichedObjectPermissions>({});
  const [unsavedIds, setUnsavedIds] = useState<ObjectGroupsIds>({});
  const [groupedObjects, setGroupedObjects] =
    useState<ObjectGroups>(emptyObjectGroups);

  const [{ data: perms, loading: pLoading, empty }, fetchObjectPermissions] =
    useFetch<ApiObjectPermissions>({
      url: `/api/${parentType}/${parentId}/object-permissions/`,
      defaultValue: {},
      onSucces: async ({ data }) => {
        const baseObjects: BaseObjects = serializeToObjectIds(data);
        const result = await enrichObjectPermissionSets(baseObjects, data);
        setOP(data);
        setObjects(result);
        setLoading(false);
      },
      onEmpty: () => {
        setLoading(false);
        setObjects({});
      },
    });

  const [objects] = useFetch({
    url: '/api/assets/?deleted=false',
    defaultValue: [],
  });

  useEffect(() => {
    setGroupedObjects(groupObjectByType(objects.data, Object.keys(perms)));
  }, [perms, objects.data]);

  const handleSelectUnsaved = async (values: ObjectGroupsIds) => {
    setUnsavedIds(values);
    const enrichedWithObject: ObjectGroups = {};

    Object.keys(values).forEach(
      key =>
        (enrichedWithObject[key] = values[key].map(id =>
          objects.data.find(
            (obj: AnyAssetApplication & { deleted: boolean }) => obj.id === id
          )
        ))
    );

    if (enrichedWithObject) {
      setUnsavedRows(
        // @ts-ignore
        (await unsavedToObjPerms(enrichedWithObject, objectPermissions)) || {}
      );
    }
  };

  const deleteUnsaved = (id: string) => {
    const newIds: ObjectGroupsIds = {};
    Object.keys(unsavedIds).forEach(
      key => (newIds[key] = unsavedIds[key].filter(uid => uid !== id))
    );
    handleSelectUnsaved(newIds);
  };
  return (
    <PermissionGuard
      redirect
      codename={[
        `change_${parentType}objectpermission`,
        `view_${parentType}objectpermission`,
      ]}
    >
      <PageContent
        title={t('Object Permissions',AUTHORIZATION_NS)}
        actions={
          <PermissionGuard codename={`change_${parentType}objectpermission`}>
            <Button
              disabled={superAdminGroup}
              onClick={() => setShowModal(true)}
              data-ta="addObjectButton"
            >
              <Flex v="center">
                <HardDrive size={14} className="mr-1" />
                {t('Add object',AUTHORIZATION_NS)}
              </Flex>
            </Button>
          </PermissionGuard>
        }
      >
        {(Object.keys(perms).length > 0 || empty) && (
          <>
            <Spin spinning={pLoading || loading || objects.loading}>
              <ObjectPermissionRows
                parentId={parentId}
                rows={existingObjects}
                unsavedRows={unsavedRows}
                fetchObjectPermissions={fetchObjectPermissions}
                userPermissions={userPermissions}
                deleteUnsaved={deleteUnsaved}
                parentType={parentType}
                setLoading={setLoading}
                loading={loading}
              />
            </Spin>
            {showModal && (
              <AddObjectModal
                parentType={parentType}
                visible={showModal}
                unsaved={[unsavedIds, handleSelectUnsaved]}
                existingObjects={Object.keys(perms)}
                hideModal={() => setShowModal(false)}
                groupedObjects={groupedObjects}
              />
            )}
          </>
        )}
      </PageContent>
    </PermissionGuard>
  );
}
type RowProps = {
  rows: EnrichedObjectPermissions;
  parentType?: ParentKey;
  parentId?: string;
  fetchObjectPermissions: Function;
  unsavedRows: EnrichedObjectPermissions | undefined;
  userPermissions: any[] | null;
  deleteUnsaved: Function;
  setLoading?: Function;
  loading?: boolean;
};

const ObjectPermissionRows = ({
  rows = {},
  parentType = 'user',
  fetchObjectPermissions,
  unsavedRows = {},
  parentId,
  userPermissions,
  deleteUnsaved,
  setLoading,
  loading,
}: RowProps) => {
  const { allPermissionsMap, contentTypes } = useContext(AppContext);
  const [hasPermission] = useUserinfo();

  const {t} = useAssetTranslation();
  const mergedRows: EnrichedObjectPermissions = { ...unsavedRows, ...rows };
  const allObjIds = Object.keys(mergedRows).filter(
    id => !!mergedRows[id].object_data
  );

  useEffect(() => {
    setOpenObjIds([...objIds, ...Object.keys(unsavedRows)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsavedRows]);

  const deleteRow = async (ids: string[]) => {
    if (setLoading) setLoading(true);
    try {
      await Api[parentType].permission.objectDelete(ids);
      fetchObjectPermissions();
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Removing permissions failed',AUTHORIZATION_NS),
        t(
          `The permission could not be removed for this ${parentType}. Please try again later.`,AUTHORIZATION_NS
        )
      );
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const handleChange = async (
    e: CheckboxChangeEvent,
    rowEntries: any,
    permissionId: number,
    content_type: number | undefined,
    parentObjectId: string,
    object_id?: string,
    permissionObject?: any
  ) => {
    if (!content_type) return;
    if (setLoading) setLoading(true);
    const enable = e.target.checked;

    const object_permission_id = enable
      ? undefined
      : rows[parentObjectId].entries.find(
          x => String(x.permission_id) === String(permissionId)
        )?.object_permission_id;

    // Add view permission on Create / Update / Delete
    let viewPermissionId: number | undefined;
    let isViewChecked: UserPermissionsAPI | undefined;

    if (permissionObject.view) {
      viewPermissionId = allPermissionsMap.get(permissionObject.view);
      isViewChecked = userPermissions?.find(
        userPerm => userPerm.permission_id === viewPermissionId
      );
    }

    const resp = await Api[parentType].permission.objectToggle(
      {
        permission:
          enable && viewPermissionId && !isViewChecked
            ? [permissionId, viewPermissionId]
            : [permissionId],
        [parentType]: parentId,
        content_type,
        object_id: object_id || parentObjectId,
      },
      object_permission_id
    );

    if (resp.status >= 200 && resp.status < 300) {
      fetchObjectPermissions();
      openNotificationWithIcon(
        enable ? notification.success : notification.warning,
        enable ? t(`Permission added`,AUTHORIZATION_NS) : t('Permission removed',AUTHORIZATION_NS),
        '',
        5
      );
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing permissions failed',AUTHORIZATION_NS),
        t(
          `The permission could not be changed for this ${parentType}. Please try again later.`,AUTHORIZATION_NS
        )
      );
      if (setLoading) setLoading(false);
    }
  };
  const [objIds, setOpenObjIds] = useState<string[]>([]);

  const filteredObjIds = allObjIds.filter(
    rowKey => !mergedRows[rowKey].object_data?.deleted
  );

  return filteredObjIds.length > 0 &&
    contentTypes.length > 0 &&
    allPermissionsMap.size > 0 &&
    userPermissions != null ? (
    <PageContainer>
      <style>
        {
          '.notclickable-collapse .ant-collapse-header { cursor: default !important }'
        }
      </style>
      <Collapse
        onChange={activeIds =>
          setOpenObjIds(
            Array.isArray(activeIds)
              ? [...activeIds, ...Object.keys(unsavedRows)]
              : [activeIds, ...Object.keys(unsavedRows)]
          )
        }
        activeKey={objIds}
        expandIconPosition="end"
      >
        {filteredObjIds.map(rowKey => {
          const row = mergedRows[rowKey];
          const contentTypeModel =
            (row.object_data as any)._contentTypeModel ??
            objectTypeToContentType(row.object_data);
          const permissionSet = assetPermissions(
            t,
            {
              obj: getContentType(contentTypeModel || '', contentTypes),
              profile: contentTypes.find(x => x.model === 'remoteprofile'),
            },
            userPermissions,
            row.profiles,
            row.object_data.id!
          );

          return (
            permissionSet &&
            row.object_data && (
              <Collapse.Panel
                className={row.isNew ? 'notclickable-collapse' : ''}
                showArrow={!row.isNew}
                header={
                  <Flex h="between" v="center">
                    <span>{row.object_data?.name}</span>
                    <Flex v="center" style={{ cursor: 'pointer' }}>
                      {row.isNew && (
                        <>
                          <span
                            className="pl-2"
                            style={{
                              color: 'red',
                            }}
                          >
                            {t('Unsaved',AUTHORIZATION_NS)}
                          </span>
                          <Trash
                            onClick={() => deleteUnsaved(row.object_data.id)}
                            className="ml-2"
                            size={17}
                            color="red"
                          />
                        </>
                      )}
                      {!row.isNew &&
                        // @ts-ignore TODO: Fix this after ISAE PR is merged with new scoped comp lib
                        !row.hasInheritedPermissions &&
                        hasPermission(
                          `change_${parentType}objectpermission`
                        ) && (
                          <Trash
                            onClick={async e => {
                              e.stopPropagation();
                              try {
                                await deleteRow(
                                  row.entries.map(x => x.object_permission_id)
                                );
                                deleteUnsaved(row.object_data.id);
                              } catch (error) {
                                console.error(error);
                                throw error;
                              }
                            }}
                            className="ml-2"
                            size={17}
                            color="red"
                          />
                        )}
                    </Flex>
                  </Flex>
                }
                key={row.object_data.id}
              >
                {permissionSet.map((permissionsSet, index) => {
                  return (
                    <Segment key={permissionsSet.label}>
                      <RowHeader style={index ? { marginTop: 24 } : undefined}>
                        <Cell style={{ paddingLeft: 16 }}>
                          {permissionsSet.label}
                        </Cell>
                        {permissionsSet.columns.map(column => (
                          <Cell
                            key={column.label}
                            width={CHECKBOX_COLUMN_WIDTH}
                            style={{
                              justifyContent: 'center',
                              textAlign: 'center',
                            }}
                          >
                            {column}
                          </Cell>
                        ))}
                      </RowHeader>
                      {permissionsSet.permissions.map(perSet => {
                        return (
                          <ObjectPermissionRow
                            key={perSet.label}
                            perSet={perSet}
                            userPermissions={row.entries}
                            disabled={
                              !hasPermission(
                                `change_${parentType}objectpermission`
                              )
                            }
                            handleChange={(
                              e: CheckboxChangeEvent,
                              userPermission: UserPermissionsAPI | undefined,
                              userPermissions: UserPermissionsAPI[],
                              permissionId: number,
                              permissionObject: PermissionSetOption
                            ) => {
                              handleChange(
                                e,
                                userPermission,
                                permissionId,
                                permissionsSet.content_type,
                                row.object_data.id,
                                perSet.object_id,
                                permissionObject
                              );
                            }}
                          />
                        );
                      })}
                    </Segment>
                  );
                })}
              </Collapse.Panel>
            )
          );
        })}
      </Collapse>
    </PageContainer>
  ) : (
    <PageContainer>
      <Flex v="center" h="center" column={true} style={{ height: '30vh' }}>
        {!loading && (
          <Empty
            description={t(
              `This ${parentType} has no object permissions yet`,AUTHORIZATION_NS
            )}
          />
        )}
      </Flex>
    </PageContainer>
  );
};
